import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
import { composeItemDescriptionWithPlate } from "../../components/invoicesAndDocuments/invoicePlateStatement";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// all sales invoices
export const fetchSalesInvoices = createAsyncThunk(
  "sales_invoices/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      const merged = { page: 1, per_page: 20, ...params };
      Object.keys(merged).forEach((key) => {
        const value = merged[key];
        if (value !== "" && value !== null && value !== undefined) {
          queryParams.append(key, value);
        }
      });
      const qs = queryParams.toString();
      const response = await axios.get(`${BASE_URL}/sales_invoices${qs ? `?${qs}` : ""}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed load data";
      return rejectWithValue(message);
    }
  }
);

export const fetchSalesInvoiceDetail = createAsyncThunk(
  "sales_invoices/fetchDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/sales_invoices/show/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data?.data ?? null;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load invoice";
      return rejectWithValue(message);
    }
  }
);

/** Build multipart body for POST /sales_invoices/:type (manual & other types) */
export function buildSalesInvoiceFormData(form) {
  const fd = new FormData();

  const append = (key, value) => {
    if (value !== null && value !== undefined && value !== "") {
      fd.append(key, String(value));
    }
  };

  [
    "winch_branch_id",
    "customer_type",
    "customer_id",
    "responsible_name",
    "service_type",
    "issue_date",
    "due_at",
    "cost_center_account_id",
    "tax_calculation",
    "tax_exemption_reason",
    "discount",
    "statement",
    "notes",
    "comment",
    "table_direction",
    "show_statement",
    "show_totals",
    "show_city",
    "show_cr",
  ].forEach((key) => append(key, form[key]));

  append("status", form.status);

  const related = form.related_manual_invoices;
  if (Array.isArray(related)) {
    related.forEach((id, index) => {
      if (id !== "" && id != null) {
        fd.append(`related_manual_invoices[${index}]`, String(id));
      }
    });
  }

  (form.items || []).forEach((item, index) => {
    const fields = item.is_compensation
      ? [
          "supply_date",
          "compensation_category_id",
          "description",
          "from_city_id",
          "to_city_id",
          "quantity",
          "unit_price",
          "tax_percentage",
          "parent_item_index",
        ]
      : [
          "supply_date",
          "service_type_id",
          "description",
          "from_city_id",
          "to_city_id",
          "quantity",
          "unit_price",
          "tax_percentage",
        ];
    fields.forEach((field) => {
      if (item.is_compensation && field === "quantity") {
        fd.append(`items[${index}][quantity]`, "1");
        return;
      }
      if (field === "parent_item_index") {
        if (item.parent_item_index !== "" && item.parent_item_index != null) {
          fd.append(
            `items[${index}][parent_item_index]`,
            String(item.parent_item_index)
          );
        }
        return;
      }
      if (item[field] !== "" && item[field] != null) {
        fd.append(`items[${index}][${field}]`, String(item[field]));
      }
    });
    fd.append(
      `items[${index}][taxable]`,
      item.taxable === true || item.taxable === 1 || item.taxable === "1" ? "1" : "0"
    );
  });

  (form.attachments || []).forEach((file, index) => {
    if (file instanceof File) {
      fd.append(`attachments[${index}]`, file);
    }
  });

  return fd;
}

/** Build multipart body for POST /sales_invoices/collected-travel */
export function buildCollectedTravelSalesInvoiceFormData(form) {
  const fd = new FormData();

  const append = (key, value) => {
    if (value !== null && value !== undefined && value !== "") {
      fd.append(key, String(value));
    }
  };

  const appendAlways = (key, value) => {
    if (value !== null && value !== undefined) {
      fd.append(key, String(value));
    }
  };

  [
    "winch_branch_id",
    "customer_type",
    "customer_id",
    "issue_date",
    "due_at",
    "cost_center_account_id",
    "tax_calculation",
    "tax_exemption_reason",
    "statement",
    "notes",
    "comment",
    "table_direction",
    "merge_same_items",
    "show_plate_statement",
  ].forEach((key) => append(key, form[key]));

  appendAlways("responsible_name", form.responsible_name ?? "");
  append("discount", form.discount);
  append("status", form.status);

  ["show_statement", "show_totals", "show_city", "show_cr", "show_shipment_no_statement"].forEach(
    (key) => {
      if (form[key] !== null && form[key] !== undefined && form[key] !== "") {
        fd.append(key, String(form[key]));
      }
    }
  );

  const seenOrderIds = new Set();
  let orderIndex = 0;
  (form.items || []).forEach((item) => {
    const orderId = item?.order_id;
    if (orderId === "" || orderId == null) return;
    const key = String(orderId);
    if (seenOrderIds.has(key)) return;
    seenOrderIds.add(key);
    append(`items[${orderIndex}][order_id]`, orderId);
    append(
      `items[${orderIndex}][description]`,
      composeItemDescriptionWithPlate(item, form.show_plate_statement)
    );
    orderIndex += 1;
  });

  (form.attachments || []).forEach((file, index) => {
    if (file instanceof File) {
      fd.append(`attachments[${index}]`, file);
    }
  });

  return fd;
}

/** Build multipart body for POST /sales_invoices/collected-rental */
export function buildCollectedRentalSalesInvoiceFormData(form) {
  const fd = new FormData();

  const append = (key, value) => {
    if (value !== null && value !== undefined && value !== "") {
      fd.append(key, String(value));
    }
  };

  const appendAlways = (key, value) => {
    if (value !== null && value !== undefined) {
      fd.append(key, String(value));
    }
  };

  [
    "winch_branch_id",
    "customer_type",
    "customer_id",
    "issue_date",
    "due_at",
    "cost_center_account_id",
    "tax_calculation",
    "tax_exemption_reason",
    "statement",
    "notes",
    "comment",
    "table_direction",
    "merge_same_items",
    "show_plate_statement",
  ].forEach((key) => append(key, form[key]));

  appendAlways("responsible_name", form.responsible_name ?? "");
  append("discount", form.discount);
  append("status", form.status);

  ["show_statement", "show_totals", "show_city", "show_cr", "show_shipment_no_statement"].forEach(
    (key) => {
      if (form[key] !== null && form[key] !== undefined && form[key] !== "") {
        fd.append(key, String(form[key]));
      }
    }
  );

  let itemIndex = 0;
  (form.items || []).forEach((item) => {
    const orderId = item?.order_id;
    if (orderId === "" || orderId == null) return;

    append(`items[${itemIndex}][order_id]`, orderId);
    append(`items[${itemIndex}][supply_date]`, item.supply_date);
    append(`items[${itemIndex}][service_type_id]`, item.service_type_id);
    append(
      `items[${itemIndex}][description]`,
      composeItemDescriptionWithPlate(item, form.show_plate_statement)
    );
    append(`items[${itemIndex}][quantity]`, item.quantity);
    append(`items[${itemIndex}][unit_price]`, item.unit_price);
    fd.append(
      `items[${itemIndex}][taxable]`,
      item?.taxable === true || item?.taxable === 1 || item?.taxable === "1"
        ? "1"
        : "0"
    );
    if (
      item?.taxable === true ||
      item?.taxable === 1 ||
      item?.taxable === "1"
    ) {
      append(`items[${itemIndex}][tax_percentage]`, item.tax_percentage || "15");
    }
    itemIndex += 1;
  });

  (form.attachments || []).forEach((file, index) => {
    if (file instanceof File) {
      fd.append(`attachments[${index}]`, file);
    }
  });

  return fd;
}

export const createSalesInvoices = createAsyncThunk(
  "sales_invoices/add",
  async ({ type, data }, { rejectWithValue }) => {
    try {
      const isFormData =
        typeof FormData !== "undefined" && data instanceof FormData;
      const response = await axios.post(
        `${BASE_URL}/sales_invoices/${type}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            ...(isFormData
              ? { "Content-Type": "multipart/form-data" }
              : { "Content-Type": "application/json" }),
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "An unknown error occurred";
      return rejectWithValue(message);
    }
  }
);

const postSalesInvoiceFormData = async (url, data, rejectWithValue) => {
  try {
    const response = await axios.post(url, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "multipart/form-data",
        Lang: i18n.language,
      },
    });
    return response.data;
  } catch (error) {
    const message = error.response?.data?.message || "An unknown error occurred";
    return rejectWithValue(message);
  }
};

/** Build multipart body for POST /sales_invoices/update-details */
export function buildUpdateSalesInvoiceDetailsFormData(form) {
  const fd = new FormData();

  const append = (key, value) => {
    if (value !== null && value !== undefined && value !== "") {
      fd.append(key, String(value));
    }
  };

  const appendAlways = (key, value) => {
    if (value !== null && value !== undefined) {
      fd.append(key, String(value));
    }
  };

  appendAlways("id", form.id);
  appendAlways("responsible_name", form.responsible_name ?? "");
  append("service_type", form.service_type);
  append("issue_date", form.issue_date);
  append("due_at", form.due_at);
  append("tax_calculation", form.tax_calculation);
  appendAlways("tax_exemption_reason", form.tax_exemption_reason ?? "");
  appendAlways("statement", form.statement ?? "");
  appendAlways("notes", form.notes ?? "");
  appendAlways("comment", form.comment ?? "");
  append("status", form.status);
  appendAlways(
    "invoice_system",
    form.invoice_system === true ||
      form.invoice_system === 1 ||
      form.invoice_system === "1"
      ? "1"
      : "0"
  );
  appendAlways(
    "send_to_customer",
    form.send_to_customer === true ||
      form.send_to_customer === 1 ||
      form.send_to_customer === "1"
      ? "1"
      : "0"
  );
  append("table_direction", form.table_direction || "ar");
  ["show_statement", "show_totals", "show_city", "show_cr"].forEach((key) => {
    const val = form[key];
    appendAlways(
      key,
      val === true || val === 1 || val === "1" ? "1" : "0"
    );
  });

  const related = form.related_manual_invoices;
  if (Array.isArray(related)) {
    related.forEach((invoiceId, index) => {
      if (invoiceId !== "" && invoiceId != null) {
        fd.append(`related_manual_invoices[${index}]`, String(invoiceId));
      }
    });
  }

  (form.attachments || []).forEach((file, index) => {
    if (file instanceof File) {
      fd.append(`attachments[${index}]`, file);
    }
  });

  return fd;
}

/**
 * Unpaid collected OR unpaid manual: POST /sales_invoices/update-details
 * — only the fields allowed for unpaid invoices (incl. items[id]+description).
 */
export function buildUpdateSalesInvoiceUnpaidDetailsFormData(form, items = []) {
  const fd = new FormData();
  const appendAlways = (key, value) => {
    fd.append(key, value === null || value === undefined ? "" : String(value));
  };
  const flag01 = (value) =>
    value === true || value === 1 || value === "1" ? "1" : "0";

  appendAlways("id", form.id);
  appendAlways("responsible_name", form.responsible_name ?? "");
  appendAlways("issue_date", form.issue_date ?? "");
  appendAlways("due_at", form.due_at ?? "");
  appendAlways("statement", form.statement ?? "");
  appendAlways("notes", form.notes ?? "");
  appendAlways("table_direction", form.table_direction || "ar");
  appendAlways("show_statement", flag01(form.show_statement));
  appendAlways("show_totals", flag01(form.show_totals));
  appendAlways("show_city", flag01(form.show_city));
  appendAlways("show_cr", flag01(form.show_cr));
  appendAlways("merge_same_items", form.merge_same_items ?? "without_merge");
  appendAlways("show_plate_statement", form.show_plate_statement ?? "hide");
  appendAlways(
    "show_shipment_no_statement",
    flag01(form.show_shipment_no_statement)
  );

  (form.attachments || []).forEach((file, index) => {
    if (file instanceof File) {
      fd.append(`attachments[${index}]`, file);
    }
  });

  (Array.isArray(items) ? items : []).forEach((item, index) => {
    appendAlways(
      `items[${index}][id]`,
      item?.id != null && item.id !== "" ? item.id : ""
    );
    appendAlways(
      `items[${index}][description]`,
      item?.description != null ? item.description : ""
    );
  });

  return fd;
}

/** @deprecated use buildUpdateSalesInvoiceUnpaidDetailsFormData */
export const buildUpdateSalesInvoiceUnpaidCollectedDetailsFormData =
  buildUpdateSalesInvoiceUnpaidDetailsFormData;

/** Build multipart body for POST /sales_invoices/update-manual-items (manual invoices) */
export function buildUpdateSalesInvoiceItemsFormData(id, items = [], status = "draft") {
  const fd = new FormData();
  fd.append("id", String(id));
  fd.append("status", String(status || "draft"));

  items.forEach((item, index) => {
    const fields = item.is_compensation
      ? [
          "supply_date",
          "compensation_category_id",
          "description",
          "from_city_id",
          "to_city_id",
          "quantity",
          "unit_price",
          "parent_item_index",
        ]
      : [
          "supply_date",
          "service_type_id",
          "description",
          "from_city_id",
          "to_city_id",
          "quantity",
          "unit_price",
        ];
    fields.forEach((field) => {
      if (item.is_compensation && field === "quantity") {
        fd.append(`items[${index}][quantity]`, "1");
        return;
      }
      if (field === "parent_item_index") {
        if (item.parent_item_index !== "" && item.parent_item_index != null) {
          fd.append(
            `items[${index}][parent_item_index]`,
            String(item.parent_item_index)
          );
        }
        return;
      }
      if (item[field] !== "" && item[field] != null) {
        fd.append(`items[${index}][${field}]`, String(item[field]));
      }
    });
    fd.append(
      `items[${index}][taxable]`,
      item.taxable === true || item.taxable === 1 || item.taxable === "1" ? "1" : "0"
    );
  });

  return fd;
}

/**
 * Full multipart body for POST /sales_invoices/update-manual-items
 * (details + items) — used when saving manual draft invoice details.
 */
export function buildUpdateSalesInvoiceManualFormData(form, items = []) {
  const fd = new FormData();
  const appendAlways = (key, value) => {
    fd.append(key, value === null || value === undefined ? "" : String(value));
  };
  const flag01 = (value) =>
    value === true || value === 1 || value === "1" ? "1" : "0";

  appendAlways("id", form.id);
  appendAlways("status", form.status ?? "unpaid");
  appendAlways("responsible_name", form.responsible_name ?? "");
  appendAlways("service_type", form.service_type ?? "");
  appendAlways("issue_date", form.issue_date ?? "");
  appendAlways("due_at", form.due_at ?? "");
  appendAlways("tax_calculation", form.tax_calculation ?? "");
  appendAlways("tax_exemption_reason", form.tax_exemption_reason ?? "");
  appendAlways("statement", form.statement ?? "");
  appendAlways("notes", form.notes ?? "");
  appendAlways("comment", form.comment ?? "");
  appendAlways("invoice_system", flag01(form.invoice_system));
  appendAlways("send_to_customer", flag01(form.send_to_customer));
  appendAlways("table_direction", form.table_direction || "ar");
  ["show_statement", "show_totals", "show_city", "show_cr"].forEach((key) => {
    appendAlways(key, flag01(form[key]));
  });
  appendAlways("merge_same_items", form.merge_same_items ?? "without_merge");
  appendAlways("show_plate_statement", form.show_plate_statement ?? "hide");
  appendAlways(
    "show_shipment_no_statement",
    flag01(form.show_shipment_no_statement ?? "0")
  );

  const related = form.related_manual_invoices;
  if (Array.isArray(related)) {
    related.forEach((invoiceId, index) => {
      if (invoiceId !== "" && invoiceId != null) {
        fd.append(`related_manual_invoices[${index}]`, String(invoiceId));
      }
    });
  }

  (items || []).forEach((item, index) => {
    const fields = item.is_compensation
      ? [
          "supply_date",
          "compensation_category_id",
          "description",
          "from_city_id",
          "to_city_id",
          "quantity",
          "unit_price",
          "parent_item_index",
        ]
      : [
          "supply_date",
          "service_type_id",
          "description",
          "from_city_id",
          "to_city_id",
          "quantity",
          "unit_price",
        ];
    fields.forEach((field) => {
      if (item.is_compensation && field === "quantity") {
        fd.append(`items[${index}][quantity]`, "1");
        return;
      }
      if (field === "parent_item_index") {
        if (item.parent_item_index !== "" && item.parent_item_index != null) {
          fd.append(
            `items[${index}][parent_item_index]`,
            String(item.parent_item_index)
          );
        }
        return;
      }
      if (item[field] !== "" && item[field] != null) {
        fd.append(`items[${index}][${field}]`, String(item[field]));
      }
    });
    fd.append(
      `items[${index}][taxable]`,
      flag01(item.taxable)
    );
  });

  const newAttachments = (form.attachments || []).filter((file) => file instanceof File);
  appendAlways(
    "replace_attachments",
    form.replace_attachments === "1" ||
      form.replace_attachments === 1 ||
      newAttachments.length > 0
      ? "1"
      : "0"
  );
  newAttachments.forEach((file, index) => {
    fd.append(`attachments[${index}]`, file);
  });

  return fd;
}

/** Build multipart body for POST /sales_invoices/update-collected-items */
export function buildUpdateSalesInvoiceCollectedItemsFormData(form, items = []) {
  const fd = new FormData();
  const appendAlways = (key, value) => {
    fd.append(key, value === null || value === undefined ? "" : String(value));
  };
  const flag01 = (value) =>
    value === true || value === 1 || value === "1" ? "1" : "0";

  appendAlways("id", form.id);
  appendAlways("status", form.status ?? "draft");
  appendAlways("responsible_name", form.responsible_name ?? "");
  appendAlways("service_type", form.service_type ?? "");
  appendAlways("issue_date", form.issue_date ?? "");
  appendAlways("due_at", form.due_at ?? "");
  appendAlways("tax_calculation", form.tax_calculation ?? "");
  appendAlways("tax_exemption_reason", form.tax_exemption_reason ?? "");
  appendAlways("statement", form.statement ?? "");
  appendAlways("notes", form.notes ?? "");
  appendAlways("comment", form.comment ?? "");
  appendAlways("invoice_system", flag01(form.invoice_system));
  appendAlways("send_to_customer", flag01(form.send_to_customer));
  appendAlways("table_direction", form.table_direction || "ar");
  appendAlways("merge_same_items", form.merge_same_items ?? "without_merge");
  appendAlways("show_plate_statement", form.show_plate_statement ?? "hide");
  ["show_statement", "show_totals", "show_city", "show_cr", "show_shipment_no_statement"].forEach(
    (key) => appendAlways(key, flag01(form[key]))
  );

  const related = form.related_manual_invoices;
  if (Array.isArray(related)) {
    related.forEach((invoiceId, index) => {
      if (invoiceId !== "" && invoiceId != null) {
        fd.append(`related_manual_invoices[${index}]`, String(invoiceId));
      }
    });
  }

  items
    .filter((item) => !item?.compensation_id)
    .forEach((item, index) => {
      if (item?.order_id != null && item.order_id !== "") {
        fd.append(`items[${index}][order_id]`, String(item.order_id));
      }
      fd.append(`items[${index}][taxable]`, flag01(item?.taxable));
    });
  const newAttachments = (form.attachments || []).filter((file) => file instanceof File);
  appendAlways(
    "replace_attachments",
    form.replace_attachments === "1" || form.replace_attachments === 1 || newAttachments.length > 0
      ? "1"
      : "0"
  );
  newAttachments.forEach((file, index) => {
    fd.append(`attachments[${index}]`, file);
  });
  return fd;
}

export const updateSalesInvoiceDetails = createAsyncThunk(
  "sales_invoices/updateDetails",
  async (data, { rejectWithValue }) =>
    postSalesInvoiceFormData(
      `${BASE_URL}/sales_invoices/update-details`,
      data,
      rejectWithValue
    )
);

export const updateSalesInvoiceItems = createAsyncThunk(
  "sales_invoices/updateItems",
  async (data, { rejectWithValue }) =>
    postSalesInvoiceFormData(
      `${BASE_URL}/sales_invoices/update-manual-items`,
      data,
      rejectWithValue
    )
);

export const updateSalesInvoiceCollectedItems = createAsyncThunk(
  "sales_invoices/updateCollectedItems",
  async (data, { rejectWithValue }) =>
    postSalesInvoiceFormData(
      `${BASE_URL}/sales_invoices/update-collected-items`,
      data,
      rejectWithValue
    )
);

export const publishSalesInvoice = createAsyncThunk(
  "sales_invoices/publish",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("id", String(invoiceId));

      const response = await axios.post(
        `${BASE_URL}/sales_invoices/publish`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to publish invoice";
      return rejectWithValue(message);
    }
  }
);

export const deleteSalesInvoice = createAsyncThunk(
  "sales_invoices/delete",
  async (id, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("id", String(id));

      const response = await axios.post(
        `${BASE_URL}/sales_invoices/delete`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return { ...(response.data || {}), id };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete invoice";
      return rejectWithValue(message);
    }
  }
);

export const allocateSalesInvoiceBond = createAsyncThunk(
  "sales_invoices/allocateBond",
  async ({ invoiceId, bonds = [] }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("id", String(invoiceId));
      bonds.forEach((bond, index) => {
        formData.append(`bonds[${index}][bond_id]`, String(bond.bond_id));
        formData.append(
          `bonds[${index}][allocated_amount]`,
          String(bond.allocated_amount)
        );
      });

      const response = await axios.post(
        `${BASE_URL}/sales_invoices/allocate-bond`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to allocate bond";
      return rejectWithValue(message);
    }
  }
);

export const releaseSalesInvoicePaymentBond = createAsyncThunk(
  "sales_invoices/releasePaymentBond",
  async ({ bond_id, sales_invoice_id }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("bond_id", String(bond_id));
      formData.append("sales_invoice_id", String(sales_invoice_id));

      const response = await axios.post(
        `${BASE_URL}/bonds/remove-sales-allocation`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to remove payment bond";
      return rejectWithValue(message);
    }
  }
);

export const releaseSalesInvoiceCreditNote = createAsyncThunk(
  "sales_invoices/releaseCreditNote",
  async ({ credit_note_id, sales_invoice_id }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("credit_note_id", String(credit_note_id));
      formData.append("sales_invoice_id", String(sales_invoice_id));

      const response = await axios.post(
        `${BASE_URL}/credit-notes/remove-purchase-allocation`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to remove credit note allocation";
      return rejectWithValue(message);
    }
  }
);

const SalesInvoiceSlice = createSlice({
  name: "sales_invoices",
  initialState: {
    sales_invoices: [],
    record: null,
    isLoading: false,
    updateLoading: false,
    publishLoading: false,
    deleteLoading: false,
    detailLoading: false,
    detailError: null,
    error: null,
    success: null,
    updateSuccess: null,
    allocateBondLoading: false,
    releasePaymentBondLoading: false,
    releaseCreditNoteLoading: false,
    successDelete: null,
    errorDelete: null,
    pagination: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
      state.updateSuccess = null;
    },
    clearSalesInvoiceDetail: (state) => {
      state.record = null;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSalesInvoices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSalesInvoices.fulfilled, (state, action) => {
        state.isLoading = false;
        const payloadData = action.payload?.data;
        // Always replace with the current page — list uses manual (server) pagination.
        state.sales_invoices = Array.isArray(payloadData?.data)
          ? payloadData.data
          : Array.isArray(payloadData)
            ? payloadData
            : [];
        state.pagination =
          payloadData?.pagination || action.payload?.pagination || null;
      })
      .addCase(fetchSalesInvoices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      .addCase(createSalesInvoices.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSalesInvoices.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Created successfully";
        const data = action.payload?.data ?? action.payload;
        const created =
          data && typeof data === "object" && !Array.isArray(data)
            ? data?.sales_invoice && typeof data.sales_invoice === "object"
              ? data.sales_invoice
              : data?.invoice && typeof data.invoice === "object"
                ? data.invoice
                : data
            : null;
        if (created?.id != null) {
          state.record = created;
        }
      })
      .addCase(createSalesInvoices.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(fetchSalesInvoiceDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchSalesInvoiceDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.record = action.payload;
      })
      .addCase(fetchSalesInvoiceDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError = action.payload || "Failed to load invoice";
      })
      .addCase(updateSalesInvoiceDetails.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateSalesInvoiceDetails.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = action.payload?.message || "Updated";
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(updateSalesInvoiceDetails.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || "Failed to update invoice";
      })
      .addCase(updateSalesInvoiceItems.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateSalesInvoiceItems.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = action.payload?.message || "Updated";
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(updateSalesInvoiceItems.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || "Failed to update items";
      })
      .addCase(updateSalesInvoiceCollectedItems.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateSalesInvoiceCollectedItems.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccess = action.payload?.message || "Updated";
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(updateSalesInvoiceCollectedItems.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload || "Failed to update collected items";
      })
      .addCase(publishSalesInvoice.pending, (state) => {
        state.publishLoading = true;
        state.error = null;
      })
      .addCase(publishSalesInvoice.fulfilled, (state, action) => {
        state.publishLoading = false;
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(publishSalesInvoice.rejected, (state, action) => {
        state.publishLoading = false;
        state.error = action.payload || "Failed to publish invoice";
      })
      .addCase(deleteSalesInvoice.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteSalesInvoice.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.success = action.payload?.message || "Deleted successfully";
        const deletedId = action.payload?.id ?? action.meta?.arg;
        if (deletedId != null) {
          state.sales_invoices = (state.sales_invoices || []).filter(
            (row) => String(row?.id) !== String(deletedId)
          );
        }
        if (state.record && String(state.record.id) === String(deletedId)) {
          state.record = null;
        }
      })
      .addCase(deleteSalesInvoice.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload || "Failed to delete invoice";
      })
      .addCase(allocateSalesInvoiceBond.pending, (state) => {
        state.allocateBondLoading = true;
      })
      .addCase(allocateSalesInvoiceBond.fulfilled, (state, action) => {
        state.allocateBondLoading = false;
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(allocateSalesInvoiceBond.rejected, (state, action) => {
        state.allocateBondLoading = false;
        state.error = action.payload || "Failed to allocate bond";
      })
      .addCase(releaseSalesInvoicePaymentBond.pending, (state) => {
        state.releasePaymentBondLoading = true;
      })
      .addCase(releaseSalesInvoicePaymentBond.fulfilled, (state) => {
        state.releasePaymentBondLoading = false;
      })
      .addCase(releaseSalesInvoicePaymentBond.rejected, (state, action) => {
        state.releasePaymentBondLoading = false;
        state.error = action.payload || "Failed to remove payment bond";
      })
      .addCase(releaseSalesInvoiceCreditNote.pending, (state) => {
        state.releaseCreditNoteLoading = true;
      })
      .addCase(releaseSalesInvoiceCreditNote.fulfilled, (state, action) => {
        state.releaseCreditNoteLoading = false;
        if (action.payload?.data) {
          state.record = action.payload.data;
        }
      })
      .addCase(releaseSalesInvoiceCreditNote.rejected, (state, action) => {
        state.releaseCreditNoteLoading = false;
        state.error =
          action.payload || "Failed to remove credit note allocation";
      });
  },
});

export const { clearState, clearSalesInvoiceDetail } = SalesInvoiceSlice.actions;
export default SalesInvoiceSlice.reducer;
